import { PasswordValidationResult } from '../../@types/shared';

/**
 * Escapes special characters in a string so they can be safely used in a regular expression.
 * @param input The string to escape.
 * @returns The escaped string.
 */
const escapeRegExp = (input: string): string => input.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');

/**
 * Checks whether the given input string contains any characters not allowed,
 * based on a default character set and an optional list of banned characters.
 * @param input The string to validate.
 * @param options Optional configuration containing a string of banned characters.
 * @returns `true` if the input contains characters outside the allowed set; otherwise, `false`.
 * @throws If banning the specified characters results in an empty allowed character set.
 */
export const hasBannedCharacters = (
  input: string,
  options?: {
    banned_characters?: string;
  }
): boolean => {
  if (input === '') return false;
  const default_allowed_characters =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789- .()'&_$+%@";
  const banned_characters: string = options?.banned_characters ?? '';
  const allowed_character_set: Set<string> = new Set(
    [...default_allowed_characters].filter((char) => !banned_characters.includes(char))
  );
  if (allowed_character_set.size === 0)
    throw new Error('No characters left in allowed character set after banning.');
  const allowed_pattern = `[${[...allowed_character_set].map(escapeRegExp).join('')}]`;
  const regex = new RegExp(`^${allowed_pattern}+$`);
  return !regex.test(input);
};

/**
 * Validates email address
 * @param {string} email Email to validate
 * @returns {boolean}
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

/**
 * Validates international phone number
 * @param {string} phone Phone number to validate
 * @param {string} regex Specific regex string to validate, otherwise default will be used
 * @returns {boolean}
 */
export const validatePhoneNumber = (phone: string, regex?: string) => {
  const regular_expression = new RegExp(regex ? regex : '^\\+[1-9]{1}[0-9]{3,14}$');
  return regular_expression.test(phone);
};

/**
 * Checks if password string includes a provided string.
 * @param password The password string.
 * @param value The value string to look for in the password.
 * @returns `true` if the password includes the provided string, `false` otherwise.
 */
const passwordIncludes = (password: string, value: string) => {
  const formatted_password: string = password.trim().split(' ').join('').toLowerCase();
  const formatted_value: string = value.trim().split(' ').join('').toLowerCase();
  return formatted_password.includes(formatted_value);
};

/**
 * Validates password
 * @param {string} password Password to validate
 * @returns {boolean}
 */
export const validatePasswordInput = (
  password: string,
  first_name: string,
  last_name: string,
  username: string
): PasswordValidationResult => {
  return {
    has_lowercase: /[a-z]/.test(password),
    has_uppercase: /[A-Z]/.test(password),
    has_number: /[0-9]/.test(password),
    has_first_name: passwordIncludes(password, first_name),
    has_last_name: passwordIncludes(password, last_name),
    has_username: passwordIncludes(password, username),
    is_required_length: password.length > 11
  };
};

/**
 * Checks whether a password satisfies the required rules and matches its confirmation.
 * @param {string} password Password to validate.
 * @param {string} first_name First name the password must not contain.
 * @param {string} last_name Last name the password must not contain.
 * @param {string} username Username the password must not contain.
 * @param {string} confirmPassword Confirmation value the password must match.
 * @returns {boolean} `true` when the password meets all rules and matches the confirmation.
 */
export const isValidPassword = (
  password: string,
  first_name: string,
  last_name: string,
  username: string,
  confirmPassword: string
): boolean => {
  const result = validatePasswordInput(password, first_name, last_name, username);
  return (
    result.has_lowercase &&
    result.has_uppercase &&
    result.has_number &&
    result.is_required_length &&
    password === confirmPassword
  );
};

/**
 * Normalizes a string by converting to lowercase and removing excess whitespace.
 * @param {string} str String to normalize.
 * @returns {string} Normalized string.
 */
export const normalizeString = (str: string): string => {
  return str.toLowerCase().replace(/\s+/g, ' ').trim();
};

/**
 * Validates if two entries are duplicates regardless of spacing and casing.
 * @param {string} existing_entry Existing entry to validate against.
 * @param {string} new_entry New entry being validated.
 * @returns {boolean}
 */
export const validateDuplicateEntries = (existing_entry: string, new_entry: string): boolean => {
  return normalizeString(existing_entry) === normalizeString(new_entry);
};
